import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { ShieldCheck, Plugs as WebhookIcon, Trash, Plus, Copy, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Sprint S-8 · 2 cards extra para /settings:
 *   - "Mi actividad" — audit events filtrados por org del usuario
 *   - "Webhooks"     — gestión CRUD para integraciones (solo admin)
 */
export function SettingsExtensions({ role }) {
  return (
    <div className="space-y-6 mt-6" data-testid="settings-extensions">
      <MyActivityCard />
      {(role === "admin" || role === "owner") && <WebhooksCard />}
    </div>
  );
}

function MyActivityCard() {
  const [data, setData] = useState({ items: [], has_more: false, next_cursor: null });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = async (cursor = null, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "30");
      if (cursor) params.set("cursor", cursor);
      if (actionFilter) params.set("action", actionFilter);
      const { data: d } = await api.get(`/me/audit-events?${params.toString()}`);
      setData((prev) => ({
        items: append ? [...prev.items, ...d.items] : d.items,
        has_more: d.has_more,
        next_cursor: d.next_cursor,
      }));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <section className="ag-card p-6" data-testid="my-activity-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="font-display text-lg font-bold tracking-tighter">Mi actividad reciente</h3>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            value={actionFilter} onChange={(e)=>setActionFilter(e.target.value)}
            placeholder="Filtrar acción (ej: auth.login)"
            className="h-9 w-56 text-xs" data-testid="my-activity-filter"
          />
          <Button onClick={()=>load()} variant="outline" className="h-9 text-xs" data-testid="my-activity-apply">
            Aplicar
          </Button>
        </div>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-3">
        Eventos relacionados con tu organización: logins, cambios de contraseña, refunds, exports y más.
      </p>
      <div className="overflow-x-auto -mx-1">
        {loading && data.items.length === 0 ? (
          <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div>
        ) : data.items.length === 0 ? (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Sin actividad reciente.</div>
        ) : (
          <table className="w-full text-xs" data-testid="my-activity-list">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
              <tr><th className="text-left px-3 py-2">Hora</th><th className="text-left px-3 py-2">Acción</th><th className="text-left px-3 py-2">Actor</th><th className="text-left px-3 py-2">IP</th><th className="text-left px-3 py-2">Resultado</th></tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`my-activity-row-${e.id}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-[var(--ag-text-dim)]">{new Date(e.ts_iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" })}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{e.action}</td>
                  <td className="px-3 py-2 truncate max-w-[200px]">{e.actor_email || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[var(--ag-text-dim)]">{e.ip || "—"}</td>
                  <td className="px-3 py-2">{e.ok ? <span className="text-emerald-400">✓ OK</span> : <span className="text-red-400">✗ FAIL</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data.has_more && (
        <div className="mt-3 text-center">
          <Button onClick={()=>load(data.next_cursor, true)} variant="outline" className="h-9 text-xs" data-testid="my-activity-load-more">
            Cargar más
          </Button>
        </div>
      )}
    </section>
  );
}

function WebhooksCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ url: "", description: "", events: ["booking.created"] });
  const [revealedSecret, setRevealedSecret] = useState(null);
  const EVENT_OPTIONS = ["booking.created", "booking.confirmed", "booking.cancelled", "payment.succeeded", "review.created"];

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/webhooks"); setItems(data.items || []); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error("URL obligatoria"); return; }
    if (form.events.length === 0) { toast.error("Selecciona al menos un evento"); return; }
    try {
      const { data } = await api.post("/webhooks", form);
      setRevealedSecret({ id: data.id, secret: data.secret_full_once });
      setForm({ url: "", description: "", events: ["booking.created"] });
      load();
      toast.success("Webhook creado");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este webhook?")) return;
    try { await api.delete(`/webhooks/${id}`); load(); toast.success("Eliminado"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const ping = async (id) => {
    try { await api.post(`/webhooks/${id}/test`, { event_type: "test.ping" }); toast.success("Ping enviado"); setTimeout(load, 1500); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggleEvent = (ev) => {
    setForm((f) => ({ ...f, events: f.events.includes(ev) ? f.events.filter((x)=>x!==ev) : [...f.events, ev] }));
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copiado al portapapeles"); }
    catch { /* ignore */ }
  };

  return (
    <section className="ag-card p-6" data-testid="webhooks-card">
      <div className="flex items-center gap-2 mb-3">
        <WebhookIcon size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-lg font-bold tracking-tighter">Webhooks · Integraciones</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4">
        Envía eventos en tiempo real a tu sistema (Zapier, n8n, CRM…). Firmamos cada POST con HMAC-SHA256 en cabecera <code className="text-[var(--ag-text)]">X-AutoGlow-Signature</code>.
      </p>

      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Input value={form.url} onChange={(e)=>setForm({...form, url: e.target.value})} placeholder="https://tu-servicio.com/webhook" className="md:col-span-2" data-testid="wh-url" />
        <Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} placeholder="Descripción (opcional)" data-testid="wh-desc" />
        <div className="md:col-span-3 flex flex-wrap gap-2" data-testid="wh-events">
          {EVENT_OPTIONS.map((ev) => (
            <button type="button" key={ev} onClick={()=>toggleEvent(ev)}
              className={`px-3 py-1.5 text-[11px] rounded-sm border font-mono ${form.events.includes(ev) ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
              data-testid={`wh-event-${ev}`}>
              {ev}
            </button>
          ))}
        </div>
        <Button type="submit" className="ag-btn-gold md:col-span-3" data-testid="wh-create">
          <Plus size={14} className="mr-1.5" /> Añadir webhook
        </Button>
      </form>

      {revealedSecret && (
        <div className="ag-card p-3 mb-4 bg-amber-500/10 border-amber-500/30" data-testid="wh-secret-banner">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Secret (solo se muestra una vez)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono text-white truncate" data-testid="wh-secret-value">{revealedSecret.secret}</code>
            <Button onClick={()=>copy(revealedSecret.secret)} variant="outline" className="h-8 text-xs" data-testid="wh-secret-copy"><Copy size={12} className="mr-1" /> Copiar</Button>
            <button onClick={()=>setRevealedSecret(null)} className="text-amber-300 hover:text-white text-xs" data-testid="wh-secret-dismiss">×</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto" data-testid="wh-list">
        {loading ? <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div> :
         items.length === 0 ? <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Sin webhooks. Añade uno arriba.</div> :
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
            <tr><th className="text-left px-3 py-2">URL</th><th className="text-left px-3 py-2">Eventos</th><th className="text-left px-3 py-2">Estado</th><th className="text-left px-3 py-2">Último envío</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`wh-row-${w.id}`}>
                <td className="px-3 py-2 max-w-[240px] truncate"><code className="text-[11px]">{w.url}</code></td>
                <td className="px-3 py-2 text-[10px]">{w.events.join(", ")}</td>
                <td className="px-3 py-2">
                  {w.last_delivery_status === "ok" ? <span className="text-emerald-400">✓ OK</span> :
                   w.last_delivery_status?.startsWith("fail") ? <span className="text-red-400">✗ {w.last_delivery_status}</span> :
                   <span className="text-[var(--ag-text-dim)]">—</span>}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono text-[var(--ag-text-dim)]">{w.last_delivery_at ? new Date(w.last_delivery_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "Nunca"}</td>
                <td className="px-3 py-2 whitespace-nowrap space-x-1">
                  <button onClick={()=>ping(w.id)} className="px-2 py-1 rounded-sm border border-[var(--ag-border)] text-[10px] uppercase hover:bg-[var(--ag-midnight-soft)]" data-testid={`wh-ping-${w.id}`}>
                    <ArrowsClockwise size={11} className="inline mr-1" /> Test
                  </button>
                  <button onClick={()=>del(w.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`wh-del-${w.id}`}><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </section>
  );
}
